/**
 * The per-item verb rules — what a verb key on an item must name before the
 * lines or arms under it can ever be reached.
 *
 * @remarks
 * Two tables, one question each way: `unsupportedVerbNarratives` (D47)
 * re-voices a refusal and must name a verb declared UNSUPPORTED;
 * `verbInteractions` (D196) performs an act and must name a supported verb of
 * the pack's own — declared, canonical, and outside the engine's roster. The
 * two are disjoint by these rules, so any verb reaches exactly one. Both run
 * against the MERGED, compiled vocabulary, because most packs inherit their
 * verbs from the base they extend. Split off `authoring-reachability.ts` at
 * the file-size cap.
 *
 * @packageDocumentation
 */

import type { ContentPackDefinition } from '@orpheus/content-schema';
import type { CompiledLexicon } from '@orpheus/parser-engine';

import { ENGINE_CLAIMED_INTENT_TOKENS, THING_REFUSED_INTENT_TOKENS } from '@orpheus/engine-runtime';

import { type ReferenceBrokenFailure } from './load-failure.js';
import { brokenReference } from './semantic-validation.js';

/**
 * D47: every per-item verb answer must be able to be heard.
 *
 * @remarks
 * Two ways an authored beat is born dead, and both are silent without this
 * check — the pack loads, the lines sit in the JSON, and the player never
 * reaches them:
 *
 * - The key names **no declared verb at all** (a typo, or a word this
 *   vocabulary does not know).
 * - The key names a verb that IS mechanized. Only D35's declared-unsupported
 *   verbs reach the refusal these lines re-voice, so a line keyed on `take`
 *   would never speak — with D234's one exception: a verb mechanized on a
 *   BODY and refused on a THING (`attack`) reaches it on every thing.
 *
 * This is the D31 precedent applied to items: the schema there refuses a
 * response POOL on a slot whose sequencing is not threaded, rather than
 * letting authored content silently never play. Same trade, same reason.
 *
 * It runs against the MERGED, compiled vocabulary rather than the pack's own
 * `vocabulary` block, because most packs inherit `knock` and its eighteen
 * siblings from the base vocabulary they extend.
 *
 * @param contentPack - The parsed pack
 * @param lexicon - The merged, compiled vocabulary (the authority on which verbs exist and which are unsupported)
 * @returns One failure per unreachable key, all collected
 */
export const validateAuthoredVerbNarratives = (
	contentPack: ContentPackDefinition,
	lexicon: CompiledLexicon,
): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];

	contentPack.items.forEach((item, itemIndex) => {
		for (const verbToken of Object.keys(item.unsupportedVerbNarratives ?? {})) {
			const referencePath = `items[${String(itemIndex)}].unsupportedVerbNarratives.${verbToken}`;
			const intent = lexicon.intentsByToken.get(verbToken);

			if (intent === undefined) {
				failures.push(
					brokenReference(referencePath, verbToken, 'no verb by that name is declared, so these lines could never be spoken'),
				);
				continue;
			}

			// D234: a verb the engine mechanizes on a BODY and refuses on a THING
			// (`attack`) reaches exactly this refusal when aimed at a thing, so
			// the key is as legal as it was while the verb was unsupported.
			if (!intent.isUnsupported && !THING_REFUSED_INTENT_TOKENS.includes(intent.canonicalToken)) {
				failures.push(
					brokenReference(
						referencePath,
						verbToken,
						'that verb is mechanized, so it never reaches the refusal these lines re-voice — author the outcome instead',
					),
				);
				continue;
			}

			// An ALIAS resolves to its intent, so it passes the two checks above
			// and then never matches: the engine looks this map up with the
			// canonical token. Caught here rather than shipped as a beat that
			// fires for nobody.
			if (intent.canonicalToken !== verbToken) {
				failures.push(
					brokenReference(
						referencePath,
						verbToken,
						`"${verbToken}" is an alias of "${intent.canonicalToken}" — key on the canonical verb, which answers every alias`,
					),
				);
			}
		}
	});

	return failures;
};

/**
 * D196: every authored verb must be one the pack's own word can reach.
 *
 * @remarks
 * The D47 rule's mirror image, for the table that ACTS rather than refuses.
 * A `verbInteractions` key is born dead in four ways, and the fourth is the
 * new one:
 *
 * - It names no declared verb at all.
 * - It names a verb declared UNSUPPORTED — those reach the D47 refusal and
 *   never narrow to an act; the two tables are disjoint by this rule.
 * - It is an alias rather than the canonical token (the lookup is canonical).
 * - It names a verb the ENGINE claims — a roster verb (`take`, `use`, …) or
 *   a whole-input directive (`save`, `nevermind`) — which the roster answers
 *   before any item's table is consulted. A pack cannot re-author what the
 *   engine already does; `use` has `useInteractions` for that.
 *
 * Judged against the MERGED vocabulary like D47, for the same reason. D251:
 * a body's table is held to the same four rules — the roster's verbs on a
 * body are the actor interceptor's (looking, the strike, the gift, the
 * search), answered before any table is read, exactly as on a thing.
 *
 * @param contentPack - The parsed pack
 * @param lexicon - The merged, compiled vocabulary
 * @returns One failure per unreachable key, all collected
 */
export const validateAuthoredVerbInteractions = (
	contentPack: ContentPackDefinition,
	lexicon: CompiledLexicon,
): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];
	const tables = [
		...contentPack.items.map((item, itemIndex) => ({ ownerPath: `items[${String(itemIndex)}]`, verbInteractions: item.verbInteractions })),
		...contentPack.actors.map((actor, actorIndex) => ({ ownerPath: `actors[${String(actorIndex)}]`, verbInteractions: actor.verbInteractions })),
	];

	for (const { ownerPath, verbInteractions } of tables) {
		for (const verbToken of Object.keys(verbInteractions ?? {})) {
			const referencePath = `${ownerPath}.verbInteractions.${verbToken}`;
			const intent = lexicon.intentsByToken.get(verbToken);

			if (intent === undefined) {
				failures.push(brokenReference(referencePath, verbToken, 'no verb by that name is declared, so these arms could never run'));
				continue;
			}

			if (intent.isUnsupported) {
				failures.push(
					brokenReference(
						referencePath,
						verbToken,
						'that verb is declared unsupported, so it refuses by name and never reaches an authored act — drop the `isUnsupported` or author `unsupportedVerbNarratives` instead',
					),
				);
				continue;
			}

			if (intent.canonicalToken !== verbToken) {
				failures.push(
					brokenReference(
						referencePath,
						verbToken,
						`"${verbToken}" is an alias of "${intent.canonicalToken}" — key on the canonical verb, which answers every alias`,
					),
				);
				continue;
			}

			if (ENGINE_CLAIMED_INTENT_TOKENS.includes(verbToken)) {
				failures.push(
					brokenReference(
						referencePath,
						verbToken,
						'that verb is the engine\'s own — the roster answers it before any table is read; author `useInteractions` for `use`, or declare a verb of the pack\'s own',
					),
				);
			}
		}
	}

	return failures;
};
